import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type {
  ImportBatchView,
  ImportCommitView,
  ImportRowStatusName,
  ImportRowView,
  ImportRowsPage,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { formatMoney } from './billing';
import {
  canCommitBatch,
  importBatchStatusLabelKeys,
  importDateFormatLabelKeys,
  importDateFormatNames,
  importKindLabelKeys,
  importKindsOf,
  importProblemKey,
  importRowStatusLabelKeys,
  importTotalLabelKeys,
  importsBasePath,
  isFileTooLarge,
  templatePath,
  type ImportScope,
} from './imports';

export interface ImportsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** People imports (the Athletes page, the default) or finance imports (the Billing page). */
  scope?: ImportScope;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

export function ImportsPanel({
  locale,
  organizationId,
  csrfToken,
  scope = 'people',
}: ImportsPanelProps): React.JSX.Element {
  const base = importsBasePath(organizationId, scope);
  const kinds = importKindsOf(scope);
  const finance = scope === 'finance';
  const [batches, setBatches] = useState<ImportBatchView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [kind, setKind] = useState<ImportBatchView['kind']>(kinds[0] ?? 'athletes');
  const [source, setSource] = useState('');
  const [dateFormat, setDateFormat] = useState<ImportBatchView['dateFormat']>('iso');
  const [file, setFile] = useState<File | null>(null);
  const [fileTooLarge, setFileTooLarge] = useState(false);
  const [staged, setStaged] = useState(false);
  const [reviewing, setReviewing] = useState<ImportBatchView | null>(null);
  const [rows, setRows] = useState<ImportRowView[]>([]);
  const [group, setGroup] = useState<ImportRowStatusName>('needs_decision');
  const [nextAfter, setNextAfter] = useState<number | null>(null);
  const [result, setResult] = useState<ImportCommitView | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // One key per batch, kept until the commit succeeds, so a retry after a dropped connection is safe.
  const commitKeys = useRef(new Map<string, string>());

  const loadBatches = useCallback(async () => {
    try {
      setBatches(await apiRequest<ImportBatchView[]>(base));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  useEffect(() => {
    void loadBatches();
  }, [loadBatches]);

  const loadRows = useCallback(
    async (batchId: string, status: ImportRowStatusName, after: number | null) => {
      try {
        const query = `?status=${status}${after === null ? '' : `&after=${String(after)}`}`;
        const page = await apiRequest<ImportRowsPage>(`${base}/${batchId}/rows${query}`);
        setRows((current) => (after === null ? page.items : [...current, ...page.items]));
        setNextAfter(page.nextAfter);
      } catch (error) {
        setErrorKey(errorKeyOf(error));
      }
    },
    [base],
  );

  async function stage(): Promise<void> {
    if (file === null) return;
    setErrorKey(null);
    setStaged(false);
    setResult(null);
    try {
      const batch = await apiRequest<ImportBatchView>(base, {
        method: 'POST',
        csrfToken,
        body: {
          kind,
          source: source.trim(),
          dateFormat,
          fileName: file.name,
          content: await file.text(),
        },
      });
      setStaged(true);
      await loadBatches();
      await review(batch);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function review(batch: ImportBatchView): Promise<void> {
    const fresh = await apiRequest<ImportBatchView>(`${base}/${batch.id}`);
    const first =
      fresh.counts.needs_decision > 0
        ? 'needs_decision'
        : fresh.counts.error > 0
          ? 'error'
          : 'ready';
    setReviewing(fresh);
    setConfirmingCancel(false);
    setGroup(first);
    await loadRows(fresh.id, first, null);
  }

  async function decide(
    row: ImportRowView,
    decision: 'create' | 'link' | 'skip',
    athleteId?: string,
  ): Promise<void> {
    if (reviewing === null) return;
    setErrorKey(null);
    try {
      await apiRequest(`${base}/${reviewing.id}/rows/${row.id}/decision`, {
        method: 'POST',
        csrfToken,
        ifMatchVersion: row.version,
        body: athleteId === undefined ? { decision } : { decision, athleteId },
      });
      await review(reviewing);
      await loadBatches();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function commit(): Promise<void> {
    if (reviewing === null) return;
    setErrorKey(null);
    const key = commitKeys.current.get(reviewing.id) ?? crypto.randomUUID();
    commitKeys.current.set(reviewing.id, key);
    try {
      const committed = await apiRequest<ImportCommitView>(`${base}/${reviewing.id}/commit`, {
        method: 'POST',
        csrfToken,
        idempotencyKey: key,
      });
      commitKeys.current.delete(reviewing.id);
      setResult(committed);
      setReviewing(null);
      setRows([]);
      await loadBatches();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function cancel(): Promise<void> {
    if (reviewing === null) return;
    setErrorKey(null);
    try {
      await apiRequest(`${base}/${reviewing.id}/cancel`, {
        method: 'POST',
        csrfToken,
        ifMatchVersion: reviewing.version,
        body: {},
      });
      setReviewing(null);
      setRows([]);
      await loadBatches();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-labelledby="imports-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="imports-title" gutterBottom>
        {translate(locale, finance ? 'billing.imports.title' : 'imports.title')}
      </Typography>
      <Typography color="text.secondary">
        {translate(locale, finance ? 'billing.imports.intro' : 'imports.intro')}
      </Typography>
      <Typography color="text.secondary" gutterBottom>
        {translate(locale, finance ? 'billing.imports.orderNote' : 'imports.athleteFirstNote')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {staged && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate(locale, 'imports.staged')}
        </Alert>
      )}
      {result !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography>{translate(locale, 'imports.result')}</Typography>
          <Typography>
            {translate(locale, 'imports.result.created')}: {result.created} ·{' '}
            {!finance && (
              <>
                {translate(locale, 'imports.result.linked')}: {result.linked} ·{' '}
                {translate(locale, 'imports.result.skipped')}: {result.skipped} ·{' '}
              </>
            )}
            {translate(locale, 'imports.result.alreadyImported')}: {result.alreadyImported}
          </Typography>
          {(result.totals ?? []).map((total) => (
            <Typography key={`${total.type}-${total.currency}`}>
              {translate(locale, importTotalLabelKeys[total.type])}:{' '}
              {formatMoney(total.amountMinor, total.currency, locale)} ({total.count}{' '}
              {translate(locale, 'imports.totals.rows')})
            </Typography>
          ))}
        </Alert>
      )}

      <Stack spacing={2} sx={{ maxWidth: 560, mb: 3 }}>
        <TextField
          select
          size="small"
          label={translate(locale, 'imports.kind')}
          value={kind}
          onChange={(event) => {
            setKind(event.target.value as ImportBatchView['kind']);
          }}
        >
          {kinds.map((name) => (
            <MenuItem key={name} value={name}>
              {translate(locale, importKindLabelKeys[name])}
            </MenuItem>
          ))}
        </TextField>
        {kind === 'guardians' && (
          <Typography color="text.secondary">
            {translate(locale, 'imports.guardianNote')}
          </Typography>
        )}
        {kind === 'opening_balances' && (
          <Typography color="text.secondary">
            {translate(locale, 'billing.imports.openingNote')}
          </Typography>
        )}
        {kind === 'payments' && (
          <Typography color="text.secondary">
            {translate(locale, 'billing.imports.paymentsNote')}
          </Typography>
        )}
        <TextField
          size="small"
          label={translate(locale, 'imports.source')}
          helperText={translate(locale, 'imports.sourceHelp')}
          value={source}
          onChange={(event) => {
            setSource(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          select
          size="small"
          label={translate(locale, 'imports.dateFormat')}
          value={dateFormat}
          onChange={(event) => {
            setDateFormat(event.target.value as ImportBatchView['dateFormat']);
          }}
        >
          {importDateFormatNames.map((name) => (
            <MenuItem key={name} value={name}>
              {translate(locale, importDateFormatLabelKeys[name])}
            </MenuItem>
          ))}
        </TextField>
        <Button component="a" href={templatePath(organizationId, kind)} download variant="text">
          {translate(locale, 'imports.template')}
        </Button>
        <Box>
          <Typography component="label" htmlFor="import-file" variant="body2">
            {translate(locale, 'imports.file')}
          </Typography>
          <input
            id="import-file"
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => {
              const chosen = event.target.files?.[0] ?? null;
              setFileTooLarge(chosen !== null && isFileTooLarge(chosen.size));
              setFile(chosen);
            }}
          />
          {fileTooLarge && (
            <Typography color="error">{translate(locale, 'imports.fileTooLarge')}</Typography>
          )}
        </Box>
        <Button
          variant="contained"
          disabled={file === null || fileTooLarge || source.trim() === ''}
          onClick={() => {
            void stage();
          }}
        >
          {translate(locale, 'imports.stage')}
        </Button>
      </Stack>

      {reviewing !== null && (
        <Box sx={{ mb: 3 }} role="region" aria-label={reviewing.fileName}>
          <Typography variant="subtitle1" component="h3">
            {reviewing.fileName} — {translate(locale, importKindLabelKeys[reviewing.kind])}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', my: 1 }}>
            {(Object.keys(importRowStatusLabelKeys) as ImportRowStatusName[])
              .filter((status) => reviewing.counts[status] > 0)
              .map((status) => (
                <Chip
                  key={status}
                  clickable
                  color={group === status ? 'primary' : 'default'}
                  label={`${translate(locale, importRowStatusLabelKeys[status])}: ${String(reviewing.counts[status])}`}
                  onClick={() => {
                    setGroup(status);
                    void loadRows(reviewing.id, status, null);
                  }}
                />
              ))}
          </Stack>
          {(reviewing.totals ?? []).length > 0 && (
            <Box sx={{ my: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {translate(locale, 'imports.totals')}
              </Typography>
              {(reviewing.totals ?? []).map((total) => (
                <Typography key={`${total.type}-${total.currency}`}>
                  {translate(locale, importTotalLabelKeys[total.type])}:{' '}
                  {formatMoney(total.amountMinor, total.currency, locale)} ({total.count}{' '}
                  {translate(locale, 'imports.totals.rows')})
                </Typography>
              ))}
            </Box>
          )}
          {rows.length === 0 && (
            <Typography color="text.secondary">
              {translate(locale, 'imports.rows.empty')}
            </Typography>
          )}
          {rows.map((row) => (
            <Box key={row.id} sx={{ borderTop: 1, borderColor: 'divider', py: 1 }}>
              <Typography>
                {translate(locale, 'imports.row')} {row.rowNumber}:{' '}
                {Object.values(row.original)
                  .filter((value) => value !== '')
                  .join(' · ')}
              </Typography>
              {row.problems.map((problem) => (
                <Typography key={`${problem.column}-${problem.code}`} color="error" variant="body2">
                  {problem.column}: {translate(locale, importProblemKey(problem.code))}
                </Typography>
              ))}
              {row.status === 'needs_decision' && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {translate(locale, 'imports.candidates')}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
                    {row.candidates.map((candidate) => (
                      <Button
                        key={candidate.athleteId}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          void decide(row, 'link', candidate.athleteId);
                        }}
                      >
                        {translate(locale, 'imports.decision.link')}: {candidate.givenName}{' '}
                        {candidate.familyName}, {candidate.dateOfBirth}
                      </Button>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        void decide(row, 'create');
                      }}
                    >
                      {translate(locale, 'imports.decision.create')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        void decide(row, 'skip');
                      }}
                    >
                      {translate(locale, 'imports.decision.skip')}
                    </Button>
                  </Stack>
                </Box>
              )}
            </Box>
          ))}
          {nextAfter !== null && (
            <Button
              onClick={() => {
                void loadRows(reviewing.id, group, nextAfter);
              }}
            >
              {translate(locale, 'imports.rows.loadMore')}
            </Button>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              disabled={!canCommitBatch(reviewing)}
              onClick={() => {
                void commit();
              }}
            >
              {translate(locale, 'imports.commit')}
            </Button>
            {!canCommitBatch(reviewing) && (
              <Typography variant="body2" color="text.secondary">
                {translate(locale, 'imports.commit.blocked')}
              </Typography>
            )}
            <Button
              color={confirmingCancel ? 'error' : 'inherit'}
              onClick={() => {
                if (confirmingCancel) void cancel();
                else setConfirmingCancel(true);
              }}
            >
              {translate(locale, confirmingCancel ? 'imports.cancel.confirm' : 'imports.cancel')}
            </Button>
            <Button
              onClick={() => {
                setReviewing(null);
                setRows([]);
              }}
            >
              {translate(locale, 'imports.close')}
            </Button>
          </Stack>
        </Box>
      )}

      <Typography variant="subtitle1" component="h3">
        {translate(locale, 'imports.list')}
      </Typography>
      {batches === null && errorKey === null && (
        <Typography>{translate(locale, 'imports.loading')}</Typography>
      )}
      {batches?.length === 0 && (
        <Typography color="text.secondary">{translate(locale, 'imports.empty')}</Typography>
      )}
      {batches?.map((batch) => (
        <Stack
          key={batch.id}
          direction="row"
          spacing={2}
          sx={{ alignItems: 'center', borderTop: 1, borderColor: 'divider', py: 1 }}
        >
          <Typography sx={{ flex: 1 }}>
            {batch.fileName} — {translate(locale, importKindLabelKeys[batch.kind])} ·{' '}
            {translate(locale, importBatchStatusLabelKeys[batch.status])}
          </Typography>
          {batch.status === 'staged' && (
            <Button
              size="small"
              onClick={() => {
                setResult(null);
                void review(batch);
              }}
            >
              {translate(locale, 'imports.review')}
            </Button>
          )}
        </Stack>
      ))}
    </Box>
  );
}
